import moment from "moment";
import { getMomentDate } from "@/util/dates";

export const DATE_FORMAT = "MM/DD/YYYY";
export const DATE_MASK = "nm/ed/zyyy";
export const DATE_FORMAT_SERVER = "YYYY-MM-DD";
export const DATE_MASK_ARRAY = ["m", "m", "d", "d", "y", "y", "y", "y"];
export const DATE_FORMAT_CHARS = {
  n: "[0-1]",
  m: "[0-9]",
  e: "[0-3]",
  d: "[0-9]",
  z: "[1-2]",
  y: "[0-9]",
};

export const SEARCH_DATES = {
  START: "startDate",
  END: "endDate",
};

export const SEARCH_DATES_SERVER = {
  START: "beginning_time",
  END: "ending_time",
};

export const SEARCH_INPUT_PLACEHOLDER = {
  START: "Start date",
  END: "End date",
  ACTIVE: "mm/dd/yyyy",
};

const today = () => getMomentDate();
const yesterday = () => today().subtract(1, "days");
const lastMonth = () => today().subtract(1, "month");
const lastYear = () => today().subtract(1, "year");

// Built per call so the presets follow the current date, not the load date.
export const getSearchDatesPresets = () => [
  {
    text: "Today",
    start: today(),
    end: today(),
  },
  {
    text: "Yesterday",
    start: yesterday(),
    end: yesterday(),
  },
  {
    text: "This month",
    start: today().startOf("month"),
    end: today(),
  },
  {
    text: "Last month",
    start: lastMonth().startOf("month"),
    end: lastMonth().endOf("month"),
  },
  {
    text: "This year",
    start: today().startOf("year"),
    end: today().endOf("year"),
  },
  {
    text: "Last year",
    start: lastYear().startOf("year"),
    end: lastYear().endOf("year"),
  },
];

export const getDefaultSearchDates = () => ({
  startDate: lastYear(),
  endDate: today(),
});

export const getCalendarKey = (currentMonth, { calendarKey, visibleMonth }) =>
  currentMonth.isSame(visibleMonth, "month") ? calendarKey : calendarKey + 1;

export const getFormattedDate = (date) => (date ? moment(date).format("L") : "");
export const getOppositeToken = (token) =>
  token === SEARCH_DATES.START ? SEARCH_DATES.END : SEARCH_DATES.START;

const YEARS_BACK = 10;
const YEARS_FORWARD = 5;

export const isCalendarIntervalValid = ({ startDate, endDate }) => {
  if ((startDate && !endDate) || (!startDate && endDate)) {
    return true;
  }
  if (startDate && endDate) {
    startDate = startDate.startOf("day");
    endDate = endDate.endOf("day");
    return startDate.isBefore(endDate);
  }
  return false;
};

export const getCalendarDate = (_date) => {
  const date = moment(_date, DATE_FORMAT, true);
  if (date.isValid()) {
    const selectedYear = date.year();
    const currentYear = getMomentDate().year();
    if (
      selectedYear > currentYear - YEARS_BACK &&
      selectedYear < currentYear + YEARS_FORWARD
    ) {
      return date;
    }
  }
  return null;
};

export const isDateEmpty = (date) => !date || date === DATE_MASK;

export const getInputObject = ({ dates, token, date }) => {
  const opposite = getOppositeToken(token);
  const date1 = dates[token];
  const date2 = dates[opposite];
  const _date1 = moment.isMoment(date1) ? date1.format(DATE_FORMAT) : "";
  return {
    [token]: _date1 !== date && isDateEmpty(date) ? "" : date,
    [opposite]: moment.isMoment(date2) ? date2.format(DATE_FORMAT) : "",
  };
};

export const getInputDates = (token, dateString, state) => {
  const date = getCalendarDate(dateString);
  const inputDates = state.dates.input;
  const calendarDates = state.dates.calendar;
  const opposite = getOppositeToken(token);
  return {
    [opposite]: inputDates[opposite],
    [token]: date ? dateString : getFormattedDate(calendarDates[token]),
  };
};

const findFirstPlaceholderIndex = (value) => {
  const placeholderPositions = [
    value.indexOf("m"),
    value.indexOf("d"),
    value.indexOf("y"),
  ].filter((position) => position >= 0);
  return placeholderPositions.length ? Math.min(...placeholderPositions) : null;
};

export const beforeMaskedValueChange = (newState) => {
  const value = newState.value;
  let selection = newState.selection;
  if (selection && selection.length === 1) {
    const index = findFirstPlaceholderIndex(value) || Math.max(0, value.length - 1);
    selection = {
      start: index,
      end: index,
      length: 1,
    };
  }
  return { value, selection };
};

export const processCalendarDates = (anchorDate, { startDate, endDate }) => {
  let start = startDate;
  let end = endDate;
  const shouldCreateRange = !end && anchorDate && anchorDate.isBefore(start);
  const shouldDropRange = end && anchorDate && anchorDate.isSameOrBefore(start);
  if (shouldCreateRange) {
    start = anchorDate;
    end = startDate;
  }
  if (shouldDropRange) {
    end = null;
  }
  return { startDate: start, endDate: end };
};
